/**
 * SiteSearch: find a site by id or name and pick it from a list of matches.
 *
 * Follows the ARIA combobox pattern with a listbox popup. Focus stays in the input;
 * the highlighted match is exposed with aria-activedescendant. Options contain no
 * interactive elements (not allowed in a listbox); the picked site's card carries the
 * "Open site" link.
 */
import { useEffect, useId, useMemo, useReducer } from 'react';
import type { ChangeEvent, KeyboardEvent, MouseEvent } from 'react';
import type { SiteMapEntry } from '../models/siteMapModels';
import { entryHealthLabel, entryTone } from '../utils/siteMapPresentation';
import { searchSites } from '../utils/siteSearch';
import { SiteMapStatusPill } from './SiteMapStatusPill';
import { effectiveActiveIndex, initialSiteSearchState, siteSearchReducer } from './siteSearchState';

interface SiteSearchProps {
  readonly entries: readonly SiteMapEntry[];
  /** Query to start with, e.g. restored when coming back to the page; read on mount */
  readonly initialQuery: string;
  readonly onPick: (siteId: string) => void;
  readonly onQueryChange: (query: string) => void;
}

function matchCountText(query: string, total: number): string {
  if (query.trim() === '') return '';
  if (total === 0) return 'No sites match';
  return total === 1 ? '1 site matches' : `${total} sites match`;
}

function optionLabel(entry: SiteMapEntry): string {
  return `${entry.site.name}, ${entry.site.siteId}, ${entryHealthLabel(entry)}`;
}

export function SiteSearch({ entries, initialQuery, onPick, onQueryChange }: SiteSearchProps): JSX.Element {
  const [state, dispatch] = useReducer(siteSearchReducer, initialQuery, initialSiteSearchState);
  const { matches, total } = useMemo(() => searchSites(entries, state.query), [entries, state.query]);

  useEffect(() => {
    onQueryChange(state.query);
  }, [state.query, onQueryChange]);

  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number): string => `${baseId}-option-${index}`;

  const expanded = state.open && matches.length > 0;
  const activeIndex = effectiveActiveIndex(state, matches.length);

  const pick = (entry: SiteMapEntry): void => {
    dispatch({ type: 'picked' });
    onPick(entry.site.siteId);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    dispatch({ type: 'query-changed', query: event.target.value });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        dispatch({
          type: 'moved',
          direction: event.key === 'ArrowDown' ? 'next' : 'previous',
          matchCount: matches.length,
        });
        return;
      case 'Enter': {
        const chosen = expanded ? matches[activeIndex ?? 0] : undefined;
        if (chosen) {
          event.preventDefault();
          pick(chosen);
        }
        return;
      }
      case 'Escape':
        if (state.open || state.query !== '') event.preventDefault();
        dispatch({ type: 'escaped' });
        return;
      default:
        return;
    }
  };

  // Keep focus (and so the open list) in the input while the pointer presses an option.
  const keepFocus = (event: MouseEvent<HTMLLIElement>): void => event.preventDefault();

  return (
    <section className="site-map-panel__section site-map-search">
      <label htmlFor={inputId} className="site-map-panel__heading">
        Find a site
      </label>
      <div className="site-map-search__field">
        <input
          id={inputId}
          className="site-map-search__input"
          type="text"
          role="combobox"
          autoComplete="off"
          spellCheck={false}
          placeholder="Site id or name"
          value={state.query}
          aria-expanded={expanded}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex === null ? undefined : optionId(activeIndex)}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onBlur={() => dispatch({ type: 'closed' })}
        />
        {expanded && (
          <ul id={listboxId} className="site-map-search__list" role="listbox" aria-label="Matching sites">
            {matches.map((entry, index) => (
              <li
                key={entry.site.siteId}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                aria-label={optionLabel(entry)}
                className={`site-map-search__option${index === activeIndex ? ' site-map-search__option--active' : ''}`}
                onMouseDown={keepFocus}
                onClick={() => pick(entry)}
              >
                <span className="site-map-search__text">
                  <span className="site-map-search__name">{entry.site.name}</span>
                  <span className="site-map-search__id">{entry.site.siteId}</span>
                </span>
                <SiteMapStatusPill tone={entryTone(entry)} label={entryHealthLabel(entry)} />
              </li>
            ))}
          </ul>
        )}
        {state.query.trim() !== '' && total === 0 && (
          // The status line below announces this; shown here for sighted users.
          <p className="site-map-panel__muted" aria-hidden="true">
            No sites match. Sites without a published location can be opened from the Sites page.
          </p>
        )}
        {expanded && total > matches.length && (
          <p className="site-map-search__more">
            {`Showing ${matches.length} of ${total}. Type more to narrow the list.`}
          </p>
        )}
      </div>
      <p className="site-map-search__status" role="status">
        {matchCountText(state.query, total)}
      </p>
    </section>
  );
}
